/**
 * SpeechRecognition wrapper for the NL capture textarea.
 * UX-AC-027..031 / AC-089..093.
 *
 * - Detects support via `'SpeechRecognition' in window || 'webkitSpeechRecognition' in window`.
 * - Returns `{ supported, transcript, interim, listening, start, stop, error }`.
 * - Graceful degradation: when unsupported, components hide the mic button.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((ev: SpeechRecognitionEventLike) => void) | null;
  onerror: ((ev: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type SpeechCtor = new () => SpeechRecognitionLike;

function getSpeechCtor(): SpeechCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export type VoiceErrorKind =
  | 'not-supported'
  | 'permission-denied'
  | 'no-speech'
  | 'service-unavailable'
  | 'unknown';

export interface VoiceState {
  supported: boolean;
  listening: boolean;
  /** Final committed text since `start()`. */
  transcript: string;
  /** Tentative interim text — italic-muted in UI. */
  interim: string;
  error: VoiceErrorKind | null;
  start: () => void;
  stop: () => void;
  /** Reset transcripts to empty. */
  reset: () => void;
}

const SILENCE_MS_DEFAULT = 4000;

export function useVoice(options?: { silenceMs?: number }): VoiceState {
  const silenceMs = options?.silenceMs ?? SILENCE_MS_DEFAULT;
  const supported = getSpeechCtor() !== null;
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<VoiceErrorKind | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const silenceTimerRef = useRef<number | null>(null);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearSilenceTimer();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* swallow — race on stop()/end() */
      }
    }
    setListening(false);
  }, [clearSilenceTimer]);

  const scheduleSilence = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = window.setTimeout(() => {
      stop();
    }, silenceMs);
  }, [clearSilenceTimer, silenceMs, stop]);

  const start = useCallback(() => {
    setError(null);
    const Ctor = getSpeechCtor();
    if (!Ctor) {
      setError('not-supported');
      return;
    }

    let r: SpeechRecognitionLike;
    try {
      r = new Ctor();
    } catch {
      setError('unknown');
      return;
    }
    r.continuous = true;
    r.interimResults = true;
    r.lang =
      (typeof navigator !== 'undefined' ? navigator.language : null) || 'en-US';

    r.onresult = (ev) => {
      let interimChunk = '';
      let newFinal = '';
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const result = ev.results[i] as SpeechRecognitionResultLike | undefined;
        if (!result) continue;
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) newFinal += text;
        else interimChunk += text;
      }
      if (newFinal) {
        setTranscript((prev) => (prev ? `${prev} ${newFinal.trim()}` : newFinal.trim()));
        setInterim('');
      } else {
        setInterim(interimChunk);
      }
      scheduleSilence();
    };

    r.onerror = (ev) => {
      const e = ev.error;
      if (e === 'not-allowed' || e === 'service-not-allowed') {
        setError('permission-denied');
      } else if (e === 'no-speech') {
        setError('no-speech');
      } else if (e === 'network') {
        setError('service-unavailable');
      } else {
        setError('unknown');
      }
    };

    r.onend = () => {
      setListening(false);
      clearSilenceTimer();
    };

    recognitionRef.current = r;
    try {
      r.start();
      setListening(true);
      scheduleSilence();
    } catch {
      setError('unknown');
      setListening(false);
    }
  }, [clearSilenceTimer, scheduleSilence]);

  const reset = useCallback(() => {
    setTranscript('');
    setInterim('');
    setError(null);
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      clearSilenceTimer();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          /* ignore */
        }
      }
    };
  }, [clearSilenceTimer]);

  return { supported, listening, transcript, interim, error, start, stop, reset };
}
